import client, { getToken } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Uploaded media is stored as "/api/files/{id}" — <img>/<video> tags can't send headers,
// so the session token rides along as a query param. External/data URLs pass through.
export const mediaSrc = (url) => {
  if (!url) return url;
  if (url.startsWith("/api/files/")) {
    const tok = window.__SUBMIT_TOKEN__;
    return tok ? `${BACKEND_URL}${url}?token=${encodeURIComponent(tok)}` : `${BACKEND_URL}${url}?auth=${encodeURIComponent(getToken() || "")}`;
  }
  return url;
};

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp", "avif", "heic", "heif", "bmp", "svg", "tif", "tiff"];
const VIDEO_EXTS = ["mp4", "m4v", "mov", "webm", "mkv", "avi", "mpeg", "mpg", "3gp", "wmv", "ogv"];

export const mediaKind = (file) => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (file.type.startsWith("image/") || IMAGE_EXTS.includes(ext)) return "image";
  if (file.type.startsWith("video/") || VIDEO_EXTS.includes(ext)) return "video";
  return "file";
};

export const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// Chunked upload: init → PUT each slice → complete. Resolves with { media, property }.
export async function uploadPropertyFile(file, propertyId, onProgress, extra = {}) {
  const { token, ...fields } = extra;
  const hdr = token ? { "X-Submit-Token": token } : {};
  const init = await client.post("/uploads/init", {
    filename: file.name, content_type: file.type, size: file.size, property_id: propertyId || "", ...fields,
  }, { headers: hdr });
  const { upload_id: uploadId, chunk_size: chunkSize } = init.data;
  const total = Math.max(1, Math.ceil(file.size / chunkSize));
  for (let i = 0; i < total; i++) {
    const slice = file.slice(i * chunkSize, Math.min(file.size, (i + 1) * chunkSize));
    let attempt = 0;
    for (;;) {
      try {
        await client.put(`/uploads/${uploadId}/chunk/${i}`, slice, {
          headers: { "Content-Type": "application/octet-stream", ...hdr },
          onUploadProgress: (ev) => onProgress?.(Math.min(99, Math.round(((i * chunkSize + (ev.loaded || 0)) / file.size) * 100))),
        });
        break;
      } catch (e) {
        if (++attempt >= 3 || e?.response?.status === 404) throw e;
        await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
  }
  const done = await client.post(`/uploads/${uploadId}/complete`, null, { headers: hdr });
  onProgress?.(100);
  return done.data;
}

// Grab a real frame from a video file in the browser (null if the codec can't be decoded here).
export function captureVideoPoster(file, at = 1) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    const done = (blob) => { URL.revokeObjectURL(url); v.removeAttribute("src"); v.load(); resolve(blob); };
    const timer = setTimeout(() => done(null), 15000);
    v.muted = true; v.playsInline = true; v.preload = "auto"; v.src = url;
    v.onerror = () => { clearTimeout(timer); done(null); };
    v.onloadedmetadata = () => { v.currentTime = Math.min(at, Math.max(0, (v.duration || 1) / 2)); };
    v.onseeked = () => {
      try {
        const c = document.createElement("canvas");
        const scale = Math.min(1, 1280 / (v.videoWidth || 1280));
        c.width = Math.round((v.videoWidth || 640) * scale); c.height = Math.round((v.videoHeight || 360) * scale);
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        c.toBlob((b) => { clearTimeout(timer); done(b); }, "image/jpeg", 0.82);
      } catch (e) { clearTimeout(timer); done(null); }
    };
  });
}

export const shareUrl = (token) => `${window.location.origin}/share/${token}`;
